import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { ArrowLeft, UsersRound } from "lucide-react";
import { Button } from "@nosible/shared/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import { Input } from "@nosible/shared/components/ui/input";
import { Label } from "@nosible/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@nosible/shared/components/ui/select";
import { trpc } from "@/admin/lib/trpc";

const ROLES = ["member", "admin", "owner"] as const;
type OrganizationRole = (typeof ROLES)[number];
type OrganizationOption = {
  id: string;
  name: string;
  slug: string;
};

export function AdminOrganizationMemberCreate() {
  const navigate = useNavigate();
  const [organizationId, setOrganizationId] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<OrganizationRole>("member");

  const { data, isLoading } = trpc.admin.listOrganizations.useQuery({
    limit: 100,
    offset: 0,
  });

  const selectedOrganization = useMemo(
    () =>
      data?.organizations.find(
        (organization: OrganizationOption) => organization.id === organizationId,
      ),
    [data, organizationId],
  );

  const inviteMutation = trpc.admin.orgInvite.useMutation({
    onSuccess: () => {
      if (selectedOrganization) {
        navigate(`/organizations/${selectedOrganization.slug}`);
      }
    },
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!organizationId || !email.trim()) return;
    inviteMutation.mutate({
      organizationId,
      email: email.trim(),
      role,
    });
  };

  return (
    <div className="admin-form-page">
      <Button variant="ghost" size="sm" asChild>
        <Link to="/organizations">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to organizations
        </Link>
      </Button>

      <div className="admin-form-page__heading">
        <UsersRound aria-hidden="true" />
        <div>
          <span>ORGANIZATIONS / PEOPLE</span>
          <h1>Add an organization member</h1>
          <p>
            Choose the customer organization, enter the person’s email address,
            and assign their operating role.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Membership details</CardTitle>
          <CardDescription>
            The person will receive an invitation to join the selected organization.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="admin-entity-form" onSubmit={handleSubmit}>
            <div className="admin-entity-form__wide">
              <Label htmlFor="member-organization">Organization</Label>
              <Select
                value={organizationId}
                onValueChange={setOrganizationId}
                disabled={isLoading || inviteMutation.isPending}
              >
                <SelectTrigger id="member-organization">
                  <SelectValue
                    placeholder={isLoading ? "Loading organizations…" : "Select organization"}
                  />
                </SelectTrigger>
                <SelectContent>
                  {data?.organizations.map((organization: OrganizationOption) => (
                    <SelectItem key={organization.id} value={organization.id}>
                      {organization.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="member-email">Email address</Label>
              <Input
                id="member-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="person@customer.com"
                disabled={inviteMutation.isPending}
                required
              />
            </div>

            <div>
              <Label htmlFor="member-role">Organization role</Label>
              <Select
                value={role}
                onValueChange={(value) => setRole(value as OrganizationRole)}
                disabled={inviteMutation.isPending}
              >
                <SelectTrigger id="member-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((value) => (
                    <SelectItem key={value} value={value}>
                      <span className="capitalize">{value}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {inviteMutation.error && (
              <p className="admin-form-error">{inviteMutation.error.message}</p>
            )}

            <div className="admin-entity-form__actions">
              <Button variant="outline" asChild>
                <Link to="/organizations">Cancel</Link>
              </Button>
              <Button
                className="brand-primary-button"
                type="submit"
                disabled={
                  inviteMutation.isPending || !organizationId || !email.trim()
                }
              >
                {inviteMutation.isPending ? "Adding…" : "Add member"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
